import { useEffect, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, Hourglass, Upload, CheckCircle2, AlertTriangle, FileText } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/**
 * Carte de vérification du justificatif de domicile.
 * Affichée dans les dashboards Habitant/Commerçant.
 * Permet l'upload d'un justificatif (image ou PDF data URL) et affiche le statut.
 */
export default function ResidenceVerificationCard({ compact = false }) {
  const { user, refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [reason, setReason] = useState(null);
  const [hasDoc, setHasDoc] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileRef = useRef(null);

  const load = () => {
    api
      .get("/user/residence-status")
      .then((r) => {
        setStatus(r.data.residence_status || "none");
        setReason(r.data.residence_rejected_reason);
        setHasDoc(!!r.data.has_doc);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    if (user) load();
  }, [user]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 800 * 1024) {
      toast.error("Fichier trop volumineux (max 800 Ko)");
      return;
    }
    if (!file.type.match(/^(image\/|application\/pdf)/)) {
      toast.error("Image ou PDF uniquement");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      await api.post("/user/residence-doc", { doc_url: dataUrl });
      toast.success("Justificatif envoyé — en attente de validation par la mairie");
      load();
      refresh && refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!user || (user.role !== "habitant" && user.role !== "commercant")) return null;
  if (loading) return null;

  const isVerified = status === "verified";
  const isPending = status === "pending";
  const isRejected = status === "rejected";

  if (isVerified && compact) {
    return (
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-mint-100 text-mint-700 text-[12px] font-bold"
        data-testid="residence-verified-pill"
      >
        <ShieldCheck size={13} /> Profil vérifié
      </div>
    );
  }

  return (
    <div
      data-testid="residence-card"
      className={`phenix-card p-4 sm:p-5 ${
        isVerified
          ? "bg-mint-50/60 border-mint-200"
          : isPending
          ? "bg-sun-50/60 border-sun-200"
          : isRejected
          ? "bg-pink-50/60 border-pink-200"
          : "bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`shrink-0 w-11 h-11 rounded-xl grid place-items-center ${
            isVerified
              ? "bg-mint-600 text-white"
              : isPending
              ? "bg-sun-500 text-white"
              : isRejected
              ? "bg-pink-500 text-white"
              : "bg-plum-700 text-white"
          }`}
        >
          {isVerified ? (
            <ShieldCheck size={20} strokeWidth={2.2} />
          ) : isPending ? (
            <Hourglass size={20} strokeWidth={2.2} />
          ) : isRejected ? (
            <ShieldAlert size={20} strokeWidth={2.2} />
          ) : (
            <FileText size={20} strokeWidth={2.2} />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display font-extrabold text-plum-900 text-[15px] mb-0.5">
            {isVerified
              ? "Profil Istréen vérifié"
              : isPending
              ? "Justificatif en attente de validation"
              : isRejected
              ? "Justificatif refusé"
              : "Justificatif de domicile requis"}
          </h3>
          <p className="text-[13px] text-plum-700/75 leading-snug">
            {isVerified ? (
              <>
                Bravo ! Vous pouvez proposer des trajets de co-voiturage et accéder à toutes les fonctions
                réservées aux Istréens.
              </>
            ) : isPending ? (
              <>Votre justificatif a bien été reçu. La mairie le validera sous peu (24-48 h ouvrées).</>
            ) : isRejected ? (
              <>
                Motif : <span className="font-semibold text-pink-600">{reason || "Document non conforme"}</span>
                . Vous pouvez en renvoyer un nouveau ci-dessous.
              </>
            ) : (
              <>
                Pour proposer un trajet de co-voiturage, ajoutez un justificatif de domicile à Istres
                (facture, attestation d&apos;assurance, avis d&apos;imposition…). La mairie vérifie sous 24-48 h.
              </>
            )}
          </p>

          {(isVerified && hasDoc) && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-mint-700 font-semibold">
              <CheckCircle2 size={11} /> Document validé par la mairie
            </div>
          )}
          {isRejected && reason && (
            <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-pink-600">
              <AlertTriangle size={11} /> {reason}
            </div>
          )}

          {!isVerified && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={handleFile}
                disabled={busy}
                className="hidden"
                data-testid="residence-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                className="inline-flex items-center gap-1.5 rounded-full bg-plum-700 hover:bg-plum-800 disabled:opacity-50 text-white px-4 py-2 text-[12px] font-bold"
                data-testid="residence-upload-btn"
              >
                <Upload size={13} /> {hasDoc ? "Renvoyer un justificatif" : "Ajouter mon justificatif"}
              </button>
              <span className="text-[11px] text-plum-700/55">JPG, PNG ou PDF — max 800 Ko</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
